import { useState, useRef, useCallback, useEffect, forwardRef, useLayoutEffect } from "react";
import LyricHighlight from "@/components/LyricHighlight";
import LyricsOverlay from "@/components/LyricsOverlay";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Play, Pause, Disc3, ListMusic, Shuffle, SkipForward, SkipBack, Share2, ExternalLink } from "lucide-react";
import ShareModal from "@/components/ShareModal";
import TrackSearch from "@/components/TrackSearch";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import OnAirTicker from "@/components/OnAirTicker";
import VinylRecord from "@/components/VinylRecord";
import VinylNeedle from "@/components/VinylNeedle";
import CurvedText from "@/components/CurvedText";
import Soundwave from "@/components/Soundwave";
import LoveLangHeader from "@/components/LoveLangHeader";
import { useTracks, Album } from "@/hooks/useTracks";
import { usePlaybackEngine, QueueTrack } from "@/hooks/usePlaybackEngine";
import ShuffleDebugOverlay from "@/components/ShuffleDebugOverlay";
// Lazy-load the chat popup — it's a non-critical overlay and ships its own subtree.

import { albumLabel } from "@/lib/albumLabels";
import { buildAbsaQuoteUrl } from "@/lib/absaHandoff";


const LANGS = ["All", "isiZulu", "isiXhosa", "English", "Sesotho"];

/* ── Autoplay / Shuffle toggle pill ── */
const PlaybackToggle = forwardRef<HTMLButtonElement, {
  label: string;
  active: boolean;
  onToggle: () => void;
  icon: React.ReactNode;
}>(function PlaybackToggle({ label, active, onToggle, icon }, _ref) {
  return (
    <button
      ref={_ref}
      onClick={onToggle}
      className={`btn-ghost-sm ${active ? "btn-ghost-active" : ""}`}
      aria-pressed={active}>
      
      {icon}
      {label}
    </button>);

});

/* ── Progress / scrub bar ── */
function ProgressBar({
  currentTime,
  duration,
  onSeek,
  formatTime,
  playing






}: {currentTime: number;duration: number;onSeek: (t: number) => void;formatTime: (t: number) => string;playing?: boolean;}) {
  const barRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const calcSeek = useCallback(
    (clientX: number) => {
      const bar = barRef.current;
      if (!bar || !duration) return;
      const { left, width } = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - left) / width));
      onSeek(ratio * duration);
    },
    [duration, onSeek]
  );

  const pct = duration > 0 ? currentTime / duration * 100 : 0;

  return (
    <div className="w-full flex flex-col items-stretch gap-2 select-none">
      {/* Scrub bar */}
      {/* Expanded hit area (44px tall) keeps the visual 1px line */}
      <div
        ref={barRef}
        className="relative w-full cursor-pointer group/bar flex items-center"
        style={{ height: 44, marginTop: -20, marginBottom: -20 }}
        onMouseDown={(e) => {dragging.current = true;calcSeek(e.clientX);}}
        onMouseMove={(e) => {if (dragging.current) calcSeek(e.clientX);}}
        onMouseUp={(e) => {dragging.current = false;calcSeek(e.clientX);}}
        onMouseLeave={() => {dragging.current = false;}}
        onTouchStart={(e) => {dragging.current = true;calcSeek(e.touches[0].clientX);}}
        onTouchMove={(e) => {if (dragging.current) calcSeek(e.touches[0].clientX);}}
        onTouchEnd={(e) => {dragging.current = false;calcSeek(e.changedTouches[0].clientX);}}>
        
        {/* Visual 1px track */}
        <div className="absolute left-0 right-0 h-px bg-border pointer-events-none" />
        {/* Fill */}
        <div
          className={`absolute left-0 h-px bg-absa-red transition-[width] duration-100 pointer-events-none ${playing ? "progress-glow" : ""}`}
          style={{ width: `${pct}%` }} />
        
        {/* Thumb */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-absa-red shadow opacity-0 group-hover/bar:opacity-100 transition-opacity pointer-events-none"
          style={{ left: `${pct}%` }} />
        
      </div>
      {/* Time labels */}
      <div className="flex justify-between">
        <span className="t-meta text-muted-foreground tabular-nums">
          {formatTime(currentTime)}
        </span>
        <span className="t-meta text-muted-foreground tabular-nums">
          {duration > 0 ? formatTime(duration) : "–:––"}
        </span>
      </div>
    </div>);

}

/* ── Shared stats helpers ── */
function readStats(key: string): { likes: number; plays: number } {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { likes: 0, plays: 0 };
}
function writeStats(key: string, stats: { likes: number; plays: number }) {
  try { localStorage.setItem(key, JSON.stringify(stats)); } catch { /* ignore */ }
}

/* ── Crate vinyl item ── */
function CrateItem({
  track,
  isPlaying,
  onActivate,
  itemRef,
  followTrack = true,
  flipButtons = false,
}: {
  track: QueueTrack;
  isPlaying: boolean;
  onActivate: () => void;
  itemRef?: React.RefObject<HTMLDivElement>;
  followTrack?: boolean;
  flipButtons?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const siteUrl = window.location.origin;
  const statsKey = `llf_stats_${track.id}`;

  // Like state — persisted per track id
  const [liked, setLiked] = useState<"like" | "unlike" | null>(() => {
    try {
      const v = localStorage.getItem(`llf_react_${track.id}`);
      return (v === "like" || v === "unlike") ? v : null;
    } catch { return null; }
  });
  const [pulseKey, setPulseKey] = useState(0);

  // Community stats
  const [stats, setStats] = useState<{ likes: number; plays: number }>(() => readStats(statsKey));

  const handleLike = (e: React.MouseEvent, val: "like" | "unlike") => {
    e.stopPropagation();
    const prev = liked;
    const next = prev === val ? null : val;
    setLiked(next);
    if (next) setPulseKey(k => k + 1);
    try { next ? localStorage.setItem(`llf_react_${track.id}`, next) : localStorage.removeItem(`llf_react_${track.id}`); }
    catch { /* ignore */ }

    // update like count
    if (val === "like") {
      const s = readStats(statsKey);
      const delta = next === "like" ? 1 : (prev === "like" ? -1 : 0);
      if (delta !== 0) {
        const updated = { ...s, likes: Math.max(0, s.likes + delta) };
        writeStats(statsKey, updated);
        setStats(updated);
      }
    }
  };

  const reducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  // Auto-scroll into view when this track becomes active — only if followTrack is on
  useEffect(() => {
    if (!isPlaying || !followTrack || !itemRef?.current) return;
    itemRef.current.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: window.innerWidth < 768 ? "nearest" : "center",
      inline: "nearest",
    });
  }, [isPlaying, followTrack]); // eslint-disable-line react-hooks/exhaustive-deps

  // Increment play count when track becomes active
  const prevPlaying = useRef(false);
  useEffect(() => {
    if (isPlaying && !prevPlaying.current) {
      const s = readStats(statsKey);
      const updated = { ...s, plays: s.plays + 1 };
      writeStats(statsKey, updated);
      setStats(updated);
    }
    prevPlaying.current = isPlaying;
  }, [isPlaying]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={itemRef}
      className="flex flex-col items-center group cursor-pointer track-enter"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onActivate}>

      <div
        className="relative mb-3"
        style={{
          transition: reducedMotion ? "none" : "transform 0.3s ease, filter 0.3s ease",
          transform: isPlaying ? "scale(1.05)" : "scale(1)",
          filter: isPlaying
            ? "drop-shadow(0 0 10px hsl(var(--absa-red) / 0.35))"
            : "none",
        }}
      >
        <VinylRecord
          playing={isPlaying}
          size={130}
          labelContent={
          <div className="flex flex-col items-center justify-center text-center px-1">
              <p className="t-meta text-muted-foreground mb-1">{track.language}</p>
              <div className="w-4 h-px bg-absa-red/50 mb-1" />
              <p className="font-sans text-[5.5px] text-muted-foreground/50 max-w-[56px] leading-tight text-center line-clamp-2">
                {track.title}
              </p>
            </div>
          } />
        
        {/* Like / Unlike buttons — top-right corner of the vinyl */}
        <button
          key={`like-${pulseKey}`}
          onClick={(e) => handleLike(e, "like")}
          aria-label="Like this track"
          className={`absolute top-1.5 right-1.5 z-20 w-5 h-5 flex items-center justify-center rounded-full transition-opacity duration-200 focus:outline-none ${liked === "like" ? "vinyl-like-pulse" : ""} ${hovered || liked === "like" ? "opacity-90" : "opacity-0"}`}
          style={{ fontSize: 11, color: liked === "like" ? "hsl(var(--absa-red))" : "hsl(var(--muted-foreground) / 0.6)" }}
        >
          ♥
        </button>
        <button
          onClick={(e) => handleLike(e, "unlike")}
          aria-label="Unlike this track"
          className={`absolute top-1.5 right-7 z-20 w-5 h-5 flex items-center justify-center rounded-full transition-opacity duration-200 focus:outline-none ${hovered || liked === "unlike" ? "opacity-90" : "opacity-0"}`}
          style={{ fontSize: 10, color: liked === "unlike" ? "hsl(var(--foreground) / 0.7)" : "hsl(var(--muted-foreground) / 0.5)" }}
        >
          ✕
        </button>

        {/* Community stats — bottom-right of the vinyl disc */}
        {(stats.plays > 0 || stats.likes > 0) && (
          <div
            className="absolute bottom-1.5 right-1.5 z-20 flex items-center gap-1.5 pointer-events-none"
            style={{ opacity: hovered ? 0.85 : 0.45, transition: "opacity 0.25s ease" }}
          >
            {stats.plays > 0 && (
              <span className="flex items-center gap-0.5" style={{ fontSize: 7.5, color: "hsl(var(--muted-foreground))", fontFamily: "var(--font-sans)", letterSpacing: "0.05em" }}>
                ▶ {stats.plays}
              </span>
            )}
            {stats.likes > 0 && (
              <span className="flex items-center gap-0.5" style={{ fontSize: 7.5, color: liked === "like" ? "hsl(var(--absa-red))" : "hsl(var(--muted-foreground))", fontFamily: "var(--font-sans)", letterSpacing: "0.05em" }}>
                ♥ {stats.likes}
              </span>
            )}
          </div>
        )}

        <div
          className={`absolute inset-0 rounded-full flex items-center justify-center transition-opacity duration-200 ${
          hovered || isPlaying ? "opacity-100" : "opacity-0"}`
          }>
          
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center btn-press bg-absa-red/90">
            
            {isPlaying ?
            <Pause className="w-3 h-3 text-primary-foreground" /> :

            <Play className="w-3 h-3 text-primary-foreground ml-0.5" />
            }
          </div>
        </div>
        {hovered &&
        <div className="absolute inset-0 pointer-events-none words-float">
            <CurvedText
            text={track.excerpt.slice(0, 32)}
            radius={57}
            startAngle={-55}
            fontSize={5.5}
            letterSpacing={1.2}
            opacity={0.4}
            className="text-foreground" />
          
          </div>
        }
      </div>

      {/* Track label */}
      <p
        className={`font-serif text-[11px] text-center leading-snug mb-1 line-clamp-2 px-1 transition-colors duration-300 ${
        isPlaying ? "text-foreground" : "text-foreground/70"}`
        }>
        
        "{track.title}"
      </p>
      <p className="t-meta text-muted-foreground text-center leading-relaxed">
        {track.author}
        {track.dedicateTo ? ` · ${track.dedicateTo}` : ""} · {track.language}
      </p>

      {/* Share + Buy buttons — appear on hover, order rotates per track */}
      <div
        className={`mt-2 flex items-center gap-3 transition-all duration-[var(--duration-base)] ${
          hovered ? "opacity-100" : "opacity-0"
        } ${flipButtons ? "rotate-[1deg]" : "-rotate-[1deg]"}`}
      >
        {(() => {
          const protectHref = buildAbsaQuoteUrl({ source: "track_card", track: { id: track.id, title: track.title, artist: track.author } }).url;
          const protectLink = (
            <a
              href={protectHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="btn-press flex items-center gap-1 text-muted-foreground/50 hover:text-absa-red transition-colors duration-[var(--duration-base)]"
              aria-label="Get funeral cover quote"
            >
              <ExternalLink className="w-3 h-3" />
              <span className="font-sans text-[8px] uppercase tracking-[0.15em]">Protect</span>
            </a>
          );
          const shareBtn = (
            <button
              onClick={(e) => {e.stopPropagation();setShareOpen(true);}}
              aria-label={`Share "${track.title}"`}
              className="btn-press flex items-center gap-1 text-muted-foreground/50 hover:text-absa-red transition-colors duration-[var(--duration-base)]"
            >
              <Share2 className="w-3 h-3" />
              <span className="font-sans text-[8px] uppercase tracking-[0.15em]">Share</span>
            </button>
          );
          return flipButtons ? <>{protectLink}{shareBtn}</> : <>{shareBtn}{protectLink}</>;
        })()}
      </div>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        url={`${siteUrl}/?track=${track.id}`}
        title={`Listen to "${track.title}" on Love Language FM`}
        description={track.excerpt || "Heard on Africa's biggest radio station — Ukhozi FM."} />
      
    </div>);

}

/* ── Per-album section ── */
function AlbumSection({
  album,
  isFirst,
  autoplay,
  shuffle,
  onToggleAutoplay,
  onToggleShuffle,
  currentId,
  playing,
  currentTime,
  duration,
  formatTime,
  playTrack,
  seek,
  skipNext,
  skipPrev,
  getPublicUrl,
  globalActiveRawTrack,
  totalDuration,
}: {album: Album;isFirst: boolean;autoplay: boolean;shuffle: boolean;onToggleAutoplay: () => void;onToggleShuffle: () => void;currentId: string | null;playing: boolean;currentTime: number;duration: number;formatTime: (t: number) => string;playTrack: (id: string) => void;seek: (t: number) => void;skipNext: () => void;skipPrev: () => void;getPublicUrl: (path: string) => string;globalActiveRawTrack?: Album['tracks'][0] | null;totalDuration?: string;}) {
  const [activeLang, setActiveLang] = useState("All");

  // Follow toggle — persisted in localStorage
  const [followTrack, setFollowTrack] = useState<boolean>(() => {
    try { const v = localStorage.getItem("llf_follow_track"); return v === null ? true : v === "1"; }
    catch { return true; }
  });
  const toggleFollow = () => setFollowTrack(v => {
    const next = !v;
    try { localStorage.setItem("llf_follow_track", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });

  // Refs map for auto-scroll: keyed by track id
  const crateRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

  const featuredTrack = album.tracks.find((t) => t.is_intro) ?? album.tracks[0] ?? null;
  // All tracks appear in the crate (including the intro) so they're all clickable.
  // The featured vinyl just mirrors the currently-active or first track.
  const crateRaw = album.tracks;

  const toQueueTrack = (t: typeof album.tracks[0]): QueueTrack => ({
    id: t.id,
    title: t.title,
    author: t.author,
    dedicateTo: t.dedicate_to,
    excerpt: t.excerpt,
    language: t.language,
    duration: t.duration,
    src: getPublicUrl(t.storage_path),
    albumId: t.album_id,
    isIntro: t.is_intro,
    trackGroupId: t.track_group_id ?? null,
  });

  const featuredItem = featuredTrack ? toQueueTrack(featuredTrack) : null;
  const crateItems: QueueTrack[] = crateRaw.map(toQueueTrack);

  const activeInAlbum = album.tracks.find((t) => t.id === currentId);
  const activeTrack = activeInAlbum ? toQueueTrack(activeInAlbum) : featuredItem;
  const featuredPlaying =
  playing && currentId !== null && album.tracks.some((t) => t.id === currentId);

  const filtered =
  activeLang === "All" ?
  crateItems :
  crateItems.filter((t) => t.language === activeLang);

  const displayTrack = activeTrack ?? featuredItem;

  const featuredLabel =
  <div className="flex flex-col items-center justify-center text-center px-3 w-full h-full">
      <p className="t-meta text-muted-foreground mb-1.5">
        {featuredPlaying ? "Now spinning" : "Up next"}
      </p>
      <div className="w-6 h-px bg-absa-red mb-2 opacity-60" />
      <p className="font-serif text-[10px] leading-tight text-foreground/80 mb-1 max-w-[90px]">
        "{displayTrack?.title ?? "—"}"
      </p>
      <p className="t-meta text-muted-foreground/60 mt-1">
        {displayTrack?.language ?? ""}
      </p>
    </div>;


  return (
    <div>
      {/* Album divider */}
      {!isFirst &&
      <div className="page-section mb-14">
          <div className="content-col flex items-center gap-4">
            <div className="flex-1 h-px bg-border" />
            <div className="flex items-center gap-2 text-muted-foreground/40">
              <Disc3 className="w-3.5 h-3.5" />
              <span className="t-meta">{albumLabel(album.id)}</span>
            </div>
            <div className="flex-1 h-px bg-border" />
          </div>
        </div>
      }

      {/* ── HERO: Featured vinyl ── */}
      <section
        className={`page-section ${isFirst ? "pt-nav-safe" : "pt-6"} pb-12 flex flex-col items-center w-full overflow-hidden`}>
        
        {isFirst &&
        <div className="animate-fade-up text-center mb-10 mt-6 w-full flex justify-center">
            <LoveLangHeader size="hero" />
          </div>
        }

        {/* Track details — sits above the vinyl */}
        <div className="w-full max-w-xs mb-6 text-center min-h-[1.5rem]">
          {displayTrack && (
            <p className="t-meta text-muted-foreground truncate">
              {displayTrack.author}
              {displayTrack.dedicateTo ? ` · ${displayTrack.dedicateTo}` : ""}
            </p>
          )}
        </div>

        {/* Vinyl + needle wrapper */}
        <div
          className="relative mb-10 w-[min(300px,82vw)]"
          style={{ height: 340 }}>
          
          <div
            className="absolute z-10"
            style={{ top: -10, right: -30, width: 160, height: 260 }}>
            
            <VinylNeedle playing={featuredPlaying} size={300} />
          </div>

          <div className="relative z-0">
            <VinylRecord
              playing={featuredPlaying}
              size={300}
              grooveGlow={featuredPlaying}
              labelContent={featuredLabel}
              onPlayToggle={() => {
                const t = activeTrack ?? featuredItem;
                if (!t) return;
                playTrack(t.id);
              }} />
            

            <div className="absolute inset-0 pointer-events-none">
              <CurvedText
                text="Heard on Africa's biggest radio station"
                radius={133}
                startAngle={-72}
                fontSize={7.5}
                letterSpacing={2.2}
                opacity={0.28}
                className="text-foreground" />
              
            </div>

            {featuredPlaying && displayTrack &&
            <div className="absolute inset-0 pointer-events-none words-float">
                <CurvedText
                text={`"${displayTrack.excerpt}"`}
                radius={126}
                startAngle={100}
                fontSize={7}
                letterSpacing={1.8}
                opacity={0.35}
                className="text-absa-red" />
              
              </div>
            }
          </div>

          {/* Centre play/pause button */}
          <button
            onClick={() => {
              const t = activeTrack ?? featuredItem;
              if (!t) return;
              playTrack(t.id);
            }}
            disabled={!featuredItem}
            className={`absolute top-1/2 left-1/2 z-20
              -translate-x-1/2 -translate-y-1/2
              w-10 h-10 rounded-full flex items-center justify-center
              btn-press transition-all duration-300
              ${
            featuredPlaying ?
            "bg-absa-red text-primary-foreground" :
            "bg-card border border-border text-muted-foreground hover:border-absa-red hover:text-absa-red"}`
            }
            aria-label={featuredPlaying ? "Pause" : "Play"}>
            
            {featuredPlaying ?
            <Pause className="w-3.5 h-3.5" /> :

            <Play className="w-3.5 h-3.5 ml-0.5" />
            }
          </button>
        </div>

        {/* ── Progress + transport ── */}
        {featuredItem &&
        <div className="w-full max-w-xs mb-6 flex flex-col items-stretch gap-4">
            <ProgressBar
            currentTime={featuredPlaying ? currentTime : 0}
            duration={featuredPlaying ? duration : 0}
            onSeek={seek}
            formatTime={formatTime}
            playing={featuredPlaying} />
          

            {/* Transport controls */}
            <div className="flex items-center justify-center gap-8">
              <button
              onClick={skipPrev}
              aria-label="Previous track"
              className="btn-press text-muted-foreground hover:text-foreground transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
              
                <SkipBack className="w-4 h-4" />
              </button>
              <button
              onClick={() => {
                const t = activeTrack ?? featuredItem;
                if (!t) return;
                playTrack(t.id);
              }}
              disabled={!featuredItem}
              aria-label={featuredPlaying ? "Pause" : "Play"}
              className={`btn-press w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${
              featuredPlaying ?
              "bg-absa-red text-primary-foreground" :
              "border border-border text-muted-foreground hover:border-absa-red hover:text-absa-red"}`
              }>
              
                {featuredPlaying ?
              <Pause className="w-3.5 h-3.5" /> :

              <Play className="w-3.5 h-3.5 ml-0.5" />
              }
              </button>
              <button
              onClick={skipNext}
              aria-label="Next track"
              className="btn-press text-muted-foreground hover:text-foreground transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
              
                <SkipForward className="w-4 h-4" />
              </button>
            </div>

          </div>
        }

        {/* Waveform */}
        <div
          className={`mb-4 transition-opacity duration-500 ${
          featuredPlaying ? "opacity-100" : "opacity-0"}`
          }>
          
          <Soundwave playing={featuredPlaying} bars={16} />
        </div>

        {/* ── Cinematic lyrics / landing copy block ── */}
        {(() => {
          // For the first album, always use the globally active track (works across shuffle/autoplay).
          // For subsequent albums, fall back to the track within that album.
          const inAlbumRawTrack = displayTrack ? album.tracks.find((t) => t.id === displayTrack.id) : null;
          const lyricsRawTrack = isFirst ? (globalActiveRawTrack ?? inAlbumRawTrack) : inAlbumRawTrack;
          const hasLyrics = lyricsRawTrack && (lyricsRawTrack.lyrics_status === "published" || lyricsRawTrack.lyrics_status === "pending");
          // Cinematic mode: first album activates whenever ANY track is playing;
          // non-first albums only activate when that album's own track is playing with lyrics.
          const showCinematic = isFirst ? playing : (featuredPlaying && !!hasLyrics);

          return (
            <div className="relative w-full max-w-xs mb-6 flex flex-col items-center">

              {/* Landing copy — dims to background when track plays */}
              {isFirst && (
                <div
                  style={{
                    transition: "opacity 0.8s ease, transform 0.8s ease",
                    opacity: showCinematic ? 0.06 : 1,
                    transform: showCinematic ? "translateY(-14px)" : "translateY(0)",
                    pointerEvents: showCinematic ? "none" : "auto",
                  }}
                >
                  <div className="flex justify-center">
                    <Link to="/dedicate" className="font-sans text-[11px] uppercase tracking-[0.22em] text-absa-red border-b border-absa-red/30 hover:border-absa-red pb-0.5 transition-colors">
                      SAY IT NOW →
                    </Link>
                  </div>
                </div>
              )}

              {/* Lyrics overlay — rises over dimmed copy when playing.
                  currentTime is always passed (not zeroed on pause) so lyrics
                  hold their exact word position when the user pauses. */}
              {lyricsRawTrack && (
                <div
                  style={{
                    position: isFirst ? "absolute" : "relative",
                    inset: isFirst ? 0 : undefined,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    opacity: isFirst ? (showCinematic ? 1 : 0) : (featuredPlaying ? 1 : 0.4),
                    transform: isFirst
                      ? (showCinematic ? "translateY(0)" : "translateY(10px)")
                      : "none",
                    pointerEvents: isFirst ? (showCinematic ? "auto" : "none") : "auto",
                    transition: "opacity 0.6s ease, transform 0.6s ease",
                    zIndex: 6,
                    width: "100%",
                  }}
                >
                  <LyricsOverlay
                    lyrics={lyricsRawTrack.lyrics}
                    lyricsStatus={lyricsRawTrack.lyrics_status ?? "none"}
                    currentTime={currentTime}
                    playing={isFirst ? playing : featuredPlaying}
                    trackId={isFirst ? (currentId ?? lyricsRawTrack.id) : displayTrack!.id}
                    trackTitle={lyricsRawTrack.title}
                  />
                </div>
              )}

              {/* For non-first albums without cinematic mode, show lyrics normally */}
              {!isFirst && !lyricsRawTrack && null}
            </div>
          );
        })()}

        {/* Non-first albums — keep the SAY IT NOW CTA away from cinematic block */}
        {isFirst && !featuredPlaying && (
          /* CTA is now inside the cinematic block above, no duplicate needed */
          null
        )}
      </section>

      {/* ── THE CRATE ── */}
      <section className={`page-section w-full overflow-hidden section-reveal ${isFirst ? "pb-28 sm:pb-24" : "pb-24"}`}>
        <div className="content-col">
          {/* Controls row */}
          <div className="flex flex-col gap-3 mb-8 sm:flex-row sm:items-center sm:justify-between">
            {isFirst ?
            <div className="flex gap-2 flex-wrap">
                <PlaybackToggle
                label="Autoplay"
                active={autoplay}
                onToggle={onToggleAutoplay}
                icon={<ListMusic className="w-2.5 h-2.5" />} />
              
                <PlaybackToggle
                label="Shuffle"
                active={shuffle}
                onToggle={onToggleShuffle}
                icon={<Shuffle className="w-2.5 h-2.5" />} />



                <PlaybackToggle
                label="Follow"
                active={followTrack}
                onToggle={toggleFollow}
                icon={<span className="text-[8px]">⦿</span>} />

                {totalDuration && (
                  <span className="text-[10px] tracking-widest text-muted-foreground font-mono tabular-nums ml-1 self-center">
                    {totalDuration}
                  </span>
                )}
              
              </div> :

            <div />
            }
            {/* Language filter pills */}
            <div className="flex gap-2 flex-wrap">
              {LANGS.map((l) =>
              <button
                key={l}
                onClick={() => setActiveLang(l)}
                className={`btn-ghost-sm ${activeLang === l ? "btn-ghost-active" : ""}`}>
                
                  {l}
                </button>
              )}
            </div>
          </div>

          {filtered.length > 0 &&
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 sm:gap-8 md:gap-10">
              {filtered.map((track, idx) => {
                if (!crateRefs.current[track.id]) {
                  crateRefs.current[track.id] = { current: null } as React.RefObject<HTMLDivElement>;
                }
                return (
                  <CrateItem
                    key={track.id}
                    track={track}
                    isPlaying={playing && currentId === track.id}
                    onActivate={() => playTrack(track.id)}
                    itemRef={crateRefs.current[track.id]}
                    followTrack={followTrack}
                    flipButtons={idx % 2 === 1}
                  />
                );
              })}
            </div>
          }

          {/* Desktop CTA */}
          {isFirst &&
          <div className="hidden sm:flex justify-center mt-16">
              <Link to="/dedicate" className="btn-ghost">
                SAY IT NOW →
              </Link>
            </div>
          }
        </div>
      </section>

      {/* Sticky mobile CTA — first album only */}
      {isFirst &&
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pt-4 bg-gradient-to-t from-background via-background/95 to-transparent pointer-events-none"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px) + 12px, 20px)" }}>
        
          <Link
          to="/dedicate"
          className="pointer-events-auto flex items-center justify-center w-full border border-absa-red/60 text-absa-red font-sans text-[11px] uppercase tracking-[0.22em] py-3.5 hover:bg-absa-red hover:text-primary-foreground transition-all duration-300">
          
            SAY IT NOW →
          </Link>
        </div>
      }
    </div>);

}

/* ── Page ── */
export default function Index({ onPlayingChange, pageReady = true }: {onPlayingChange?: (playing: boolean) => void; pageReady?: boolean;}) {
  const { publicAlbums: albums, loading, getPublicUrl, totalDuration } = useTracks();

  const {
    currentId,
    playing,
    autoplay,
    shuffle,
    queue,
    queueVersion,
    currentTime,
    duration,
    formatTime,
    playTrack,
    pauseAll,
    seek,
    skipNext,
    skipPrev,
    toggleAutoplay,
    toggleShuffle
  } = usePlaybackEngine(albums, getPublicUrl, pageReady);

  // Propagate playing state up to App so global PlayingContext stays in sync
  useEffect(() => {onPlayingChange?.(playing);}, [playing, onPlayingChange]);

  // ── Persist & restore player state across visits ──
  // localStorage key holds { id, t, playing }. We restore once the queue is
  // populated, then continuously save the latest position/state so a returning
  // visitor lands on the same track at the same timestamp in the same play state.
  const PLAYER_STATE_KEY = "llfm:player-state";
  const restoredRef = useRef(false);

  // Restore on first ready render
  useEffect(() => {
    if (restoredRef.current) return;
    if (!pageReady || queue.length === 0) return;
    let saved: { id?: string; t?: number; playing?: boolean } | null = null;
    try {
      const raw = localStorage.getItem(PLAYER_STATE_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch { /* ignore */ }
    if (!saved?.id) { restoredRef.current = true; return; }
    const exists = queue.some((t) => t.id === saved!.id);
    if (!exists) { restoredRef.current = true; return; }
    restoredRef.current = true;
    // Switch to the saved track if engine picked something else
    if (currentId !== saved.id) playTrack(saved.id);
    // Seek after the engine has had a tick to attach the audio element
    const seekT = typeof saved.t === "number" && isFinite(saved.t) ? saved.t : 0;
    if (seekT > 1) {
      window.setTimeout(() => seek(seekT), 250);
    }
    // Always autoplay on visit — do not restore prior pause state.
  }, [pageReady, queue, currentId, playTrack, seek, pauseAll]);

  // Save on change (throttled to once / 2s by only writing when ts changes meaningfully)
  const lastSavedRef = useRef(0);
  useEffect(() => {
    if (!restoredRef.current) return;
    if (!currentId) return;
    const now = Date.now();
    if (now - lastSavedRef.current < 2000) return;
    lastSavedRef.current = now;
    try {
      localStorage.setItem(
        PLAYER_STATE_KEY,
        JSON.stringify({ id: currentId, t: currentTime, playing })
      );
    } catch { /* ignore quota */ }
  }, [currentId, currentTime, playing]);

  // Flush on tab hide / unload so we never lose the latest position
  useEffect(() => {
    const flush = () => {
      if (!currentId) return;
      try {
        localStorage.setItem(
          PLAYER_STATE_KEY,
          JSON.stringify({ id: currentId, t: currentTime, playing })
        );
      } catch { /* ignore */ }
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flush);
    };
  }, [currentId, currentTime, playing]);



  // Globally active raw track — feeds lyrics to the first AlbumSection
  // even when shuffle plays a track from a different album.
  const globalActiveRawTrack = currentId
    ? albums.flatMap(a => a.tracks).find(t => t.id === currentId) ?? null
    : null;

  const OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/Ckt3SasaxnTnb4KWB5nJnhtfL7w2/social-images/social-1772709622912-Malume.webp";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Love Language FM — Say it while you still can.</title>
        <meta name="description" content="Dedicate a song to someone you love. 30 minutes later you're debuting on the radio. Heard on Africa's biggest station — Ukhozi FM." />
        <link rel="canonical" href="https://lovelanguage.fm/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lovelanguage.fm/" />
        <meta property="og:title" content="Love Language FM — Say it while you still can." />
        <meta property="og:description" content="Dedicate a song to someone you love. 30 minutes later you're debuting on the radio. Heard on Africa's biggest station — Ukhozi FM." />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Love Language FM – The online radio station made entirely out of your words." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Love Language FM — Say it while you still can." />
        <meta name="twitter:description" content="Dedicate a song to someone you love. 30 minutes later you're debuting on the radio. Heard on Africa's biggest station — Ukhozi FM." />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RadioBroadcastService",
          name: "Love Language FM",
          alternateName: "Sofa silahlane FM",
          url: "https://lovelanguage.fm/",
          description: "Dedicate a song to someone you love. 30 minutes later you're debuting on the radio. Heard on Africa's biggest station — Ukhozi FM.",
          inLanguage: ["zu", "en"],
          areaServed: { "@type": "Country", name: "South Africa" },
          broadcastAffiliateOf: { "@type": "RadioStation", name: "Ukhozi FM" },
          provider: { "@type": "Organization", name: "Absa Life", url: "https://www.absa.co.za/" },
          potentialAction: {
            "@type": "CreateAction",
            name: "Dedicate a song",
            target: "https://lovelanguage.fm/dedicate",
          },
        })}</script>
      </Helmet>
      <SiteNav />
      <main id="main">
      <OnAirTicker />

      {/* Global track search */}
      {!loading && (
        <div className="page-section pt-4 pb-2">
          <div className="content-col flex justify-center">
            <TrackSearch
              tracks={albums.flatMap(a => a.tracks)}
              currentId={currentId}
              playing={playing}
              onPlay={playTrack}
            />
          </div>
        </div>
      )}

      {loading &&
      <div className="flex justify-center py-32">
          <div className="w-5 h-5 rounded-full border border-absa-red/40 border-t-absa-red animate-spin" />
        </div>
      }

      {!loading &&
      albums.map((album, i) =>
      <AlbumSection
        key={album.id}
        album={album}
        isFirst={i === 0}
        autoplay={autoplay}
        shuffle={shuffle}
        onToggleAutoplay={toggleAutoplay}
        onToggleShuffle={toggleShuffle}
        currentId={currentId}
        playing={playing}
        currentTime={currentTime}
        duration={duration}
        formatTime={formatTime}
        playTrack={playTrack}
        seek={seek}
        skipNext={skipNext}
        skipPrev={skipPrev}
        getPublicUrl={getPublicUrl}
        globalActiveRawTrack={globalActiveRawTrack}
        totalDuration={i === 0 ? totalDuration : undefined} />

      )}

      {/* Empty state */}
      {!loading && albums.length === 0 &&
      <section className="pt-32 pb-16 page-section flex flex-col items-center">
          <div className="animate-fade-up text-center mb-10 mt-6 flex justify-center">
            <LoveLangHeader size="hero" />
          </div>
          <p className="font-serif italic text-muted-foreground text-sm text-center max-w-xs leading-relaxed mb-8">
            Call in. Leave a dedication. 30 min later — your words become a hit song on the radio.
          </p>
          <Link to="/dedicate" className="btn-ghost">
            SAY IT NOW →
          </Link>
        </section>
      }
      </main>

      <SiteFooter />

      {/* Admin-only shuffle debug overlay — invisible to regular visitors */}
      <ShuffleDebugOverlay queue={queue} currentId={currentId} queueVersion={queueVersion} />
    </div>);

}